/**
 * Employee form helpers shared by the Employees tab and the Candidates
 * panel (a candidate record is the same shape as an employee, which is
 * what makes hiring a plain move between collections).
 */

export const EMPTY_EMPLOYEE_FORM = {
  user_id: '', status: 'active', name: '',
  first_name: '', middle_name: '', last_name: '',
  email: '', phone: '', department: '', position: '',
  education: '', occupation: '',
  position_1: '', position_2: '', position_3: '', address: '', country: '',
  url: '',
  start_date: new Date(), contract_number: '', contract_date: null,
  birth_date: null, birth_place: '', personal_id_number: '', id_card_number: '', id_card_expiry_date: null,
  driving_licence_number: '', driving_licence_expiry: null,
  passport_number: '', passport_issue_date: null, passport_expiry_date: null,
  criminal_record: '', cr_cert_issue_date: null,
  business_name: '', business_address: '', tax_id_number: '', regon_number: '',
  bank_account_pln: '', bank_account_eur: '', bank_account_gbp: '', bank_account_usd: '',
  bank_swift_pln: '', bank_swift_eur: '', bank_swift_gbp: '', bank_swift_usd: '',
  // Per-employee invoice numbering (Phase 3)
  invoice_number_format: '', invoice_number_next: null, invoice_number_year: null,
  pkd_number: '',
  a1_certificate: '', a1_expiry_date: null, el_cert: '', el_cert_expiry_date: null,
  scissor_lift_cert: '', scissor_lift_expiry_date: null, boom_lift_cert: '', boom_lift_expiry_date: null,
  osha_cert: '', osha_expiry_date: null,
  electrical_certs: [], scissor_lift_certs: [], boom_lift_certs: [], osha_certs: [], health_safety_certs: [],
  visa_type: '', eu_visa: '', eu_visa_type: '', eu_visa_expiry_date: null,
  us_visa: '', us_visa_type: '', us_visa_expiry_date: null,
  uk_work_permit: '', uk_work_permit_type: '', uk_work_permit_expiry_date: null,
  employee_code: '', hourly_rate: 0,
  hourly_rate_1: null, currency_1: '', hourly_rate_2: null, currency_2: '', hourly_rate_3: null, currency_3: '',
  hire_date: new Date(),
  recruitment_notes: '',
  emergency_contact: { surname: '', name: '', phone: '', email: '', relationship: '' }
};

export const DATE_FIELDS = [
  'start_date', 'contract_date', 'birth_date', 'id_card_expiry_date',
  'driving_licence_expiry',
  'passport_issue_date', 'passport_expiry_date', 'cr_cert_issue_date',
  'a1_expiry_date', 'el_cert_expiry_date', 'scissor_lift_expiry_date',
  'boom_lift_expiry_date', 'osha_expiry_date',
  'eu_visa_expiry_date', 'us_visa_expiry_date',
  'uk_work_permit_expiry_date', 'hire_date'
];

/**
 * Pin a Date to noon UTC of the user-picked calendar day, then serialize.
 *
 * Every field in DATE_FIELDS represents a *calendar date* (birthday,
 * expiry, hire day) — the clock time is meaningless. React-day-picker
 * hands back a `Date` at local-midnight; naive `toISOString()` then
 * converts that to the previous UTC day in every positive timezone
 * (e.g. Europe/Warsaw UTC+2 turns "July 6 00:00" into "July 5 22:00Z"),
 * so the day round-trips as one earlier. Pinning to 12:00 UTC of the
 * picked calendar day keeps the day stable in every timezone within
 * ±12h of UTC — covers all real business locales.
 */
const toNoonUtcIso = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return null;
  return new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate(), 12, 0, 0)).toISOString();
};

export const prepareSubmitData = (form) => {
  const data = { ...form };
  DATE_FIELDS.forEach(f => {
    data[f] = toNoonUtcIso(data[f]);
  });
  return data;
};

export const populateFormFromEmployee = (emp) => ({
  user_id: emp.user_id || '', status: emp.status || 'active',
  name: emp.name || '',
  first_name: emp.first_name || '',
  middle_name: emp.middle_name || '',
  last_name: emp.last_name || '',
  email: emp.email || '', phone: emp.phone || '',
  education: emp.education || '', occupation: emp.occupation || '',
  department: emp.department || '', position: emp.position || '',
  position_1: emp.position_1 || '', position_2: emp.position_2 || '', position_3: emp.position_3 || '',
  address: emp.address || '', country: emp.country || '',
  url: emp.url || '',
  start_date: emp.start_date ? new Date(emp.start_date) : new Date(),
  contract_number: emp.contract_number || '',
  contract_date: emp.contract_date ? new Date(emp.contract_date) : null,
  birth_date: emp.birth_date ? new Date(emp.birth_date) : null,
  birth_place: emp.birth_place || '',
  personal_id_number: emp.personal_id_number || '',
  id_card_number: emp.id_card_number || '',
  id_card_expiry_date: emp.id_card_expiry_date ? new Date(emp.id_card_expiry_date) : null,
  driving_licence_number: emp.driving_licence_number || '',
  driving_licence_expiry: emp.driving_licence_expiry ? new Date(emp.driving_licence_expiry) : null,
  passport_number: emp.passport_number || '',
  passport_issue_date: emp.passport_issue_date ? new Date(emp.passport_issue_date) : null,
  passport_expiry_date: emp.passport_expiry_date ? new Date(emp.passport_expiry_date) : null,
  criminal_record: emp.criminal_record || '',
  cr_cert_issue_date: emp.cr_cert_issue_date ? new Date(emp.cr_cert_issue_date) : null,
  business_name: emp.business_name || '', business_address: emp.business_address || '',
  tax_id_number: emp.tax_id_number || '', regon_number: emp.regon_number || '',
  invoice_number_format: emp.invoice_number_format || '',
  invoice_number_next: emp.invoice_number_next ?? null,
  invoice_number_year: emp.invoice_number_year ?? null,
  bank_account_pln: emp.bank_account_pln || emp.bank_account || '',
  bank_account_eur: emp.bank_account_eur || '',
  bank_account_gbp: emp.bank_account_gbp || '',
  bank_account_usd: emp.bank_account_usd || '',
  bank_swift_pln: emp.bank_swift_pln || '',
  bank_swift_eur: emp.bank_swift_eur || '',
  bank_swift_gbp: emp.bank_swift_gbp || '',
  bank_swift_usd: emp.bank_swift_usd || '',
  pkd_number: emp.pkd_number || '',
  a1_certificate: emp.a1_certificate || '',
  a1_expiry_date: emp.a1_expiry_date ? new Date(emp.a1_expiry_date) : null,
  el_cert: emp.el_cert || '',
  el_cert_expiry_date: emp.el_cert_expiry_date ? new Date(emp.el_cert_expiry_date) : null,
  scissor_lift_cert: emp.scissor_lift_cert || '',
  scissor_lift_expiry_date: emp.scissor_lift_expiry_date ? new Date(emp.scissor_lift_expiry_date) : null,
  boom_lift_cert: emp.boom_lift_cert || '',
  boom_lift_expiry_date: emp.boom_lift_expiry_date ? new Date(emp.boom_lift_expiry_date) : null,
  osha_cert: emp.osha_cert || '',
  osha_expiry_date: emp.osha_expiry_date ? new Date(emp.osha_expiry_date) : null,
  electrical_certs: emp.electrical_certs || [], scissor_lift_certs: emp.scissor_lift_certs || [],
  boom_lift_certs: emp.boom_lift_certs || [], osha_certs: emp.osha_certs || [],
  health_safety_certs: emp.health_safety_certs || [],
  visa_type: emp.visa_type || '',
  eu_visa: emp.eu_visa || '', eu_visa_type: emp.eu_visa_type || '',
  eu_visa_expiry_date: emp.eu_visa_expiry_date ? new Date(emp.eu_visa_expiry_date) : null,
  us_visa: emp.us_visa || '', us_visa_type: emp.us_visa_type || '',
  us_visa_expiry_date: emp.us_visa_expiry_date ? new Date(emp.us_visa_expiry_date) : null,
  uk_work_permit: emp.uk_work_permit || '', uk_work_permit_type: emp.uk_work_permit_type || '',
  uk_work_permit_expiry_date: emp.uk_work_permit_expiry_date ? new Date(emp.uk_work_permit_expiry_date) : null,
  employee_code: emp.employee_code || '', hourly_rate: emp.hourly_rate || 0,
  hourly_rate_1: emp.hourly_rate_1 || null, currency_1: emp.currency_1 || '',
  hourly_rate_2: emp.hourly_rate_2 || null, currency_2: emp.currency_2 || '',
  hourly_rate_3: emp.hourly_rate_3 || null, currency_3: emp.currency_3 || '',
  hire_date: emp.hire_date ? new Date(emp.hire_date) : new Date(),
  recruitment_notes: emp.recruitment_notes || '',
  emergency_contact: emp.emergency_contact || { surname: '', name: '', phone: '', email: '', relationship: '' }
});
